import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, TrendUp, Star, CheckCircle } from "@phosphor-icons/react";
import SEO from "@/components/SEO";
import { fetchAIItem, fetchAISection } from "@/lib/api";

const scoreColor = (s) =>
  s >= 90 ? "text-green-600 bg-green-50 border-green-200"
  : s >= 75 ? "text-blue-600 bg-blue-50 border-blue-200"
  : "text-slate-600 bg-slate-50 border-slate-200";

function RecommendedToolCard({ tool: t }) {
  return (
    <div className="bg-white border border-slate-200 hover:border-blue-300 rounded-2xl p-5 flex flex-col gap-3 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5">
      <div className="flex items-start gap-3">
        <span className="text-3xl shrink-0" aria-hidden="true">{t.emoji || "🤖"}</span>
        <div className="min-w-0">
          <p className="font-bold text-slate-900 leading-snug">{t.name}</p>
          <span className="inline-block mt-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100">
            {t.category}
          </span>
        </div>
        {t.score != null && (
          <span className={`ml-auto shrink-0 text-xs font-bold px-2 py-1 rounded-lg border ${scoreColor(t.score)}`}>
            {t.score}
          </span>
        )}
      </div>
      {t.description && (
        <p className="text-sm text-slate-500 line-clamp-2 leading-relaxed">{t.description}</p>
      )}
      <div className="flex gap-2 mt-auto pt-1">
        <Link
          to={`/go/${t.slug}`}
          className="flex-1 inline-flex items-center justify-center gap-1.5 bg-green-600 hover:bg-green-700 text-white font-semibold px-3 py-2 text-xs rounded-lg transition-colors"
        >
          Try → <ArrowUpRight size={11} aria-hidden="true" />
        </Link>
        <Link
          to={`/ai-tools/${t.slug}`}
          className="flex-1 inline-flex items-center justify-center gap-1 border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold px-3 py-2 text-xs rounded-lg transition-colors"
        >
          Learn more <ArrowRight size={11} aria-hidden="true" />
        </Link>
      </div>
    </div>
  );
}

function buildToolSchema(tool) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: tool.name,
    description: tool.description,
    url: `https://trendming.com/ai-tools/${tool.slug}`,
    applicationCategory: "AIApplication",
    operatingSystem: "Web",
    offers: {
      "@type": "Offer",
      price: (tool.pricing || "").includes("Free") ? "0" : undefined,
      priceCurrency: "USD",
      description: tool.pricing,
    },
    author: { "@type": "Organization", name: tool.by },
  };
}

function SkeletonDetail() {
  return (
    <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-10 md:py-14 animate-pulse">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8">
            <div className="flex gap-4 mb-4">
              <div className="w-14 h-14 bg-slate-200 rounded-xl shrink-0" />
              <div className="flex-1">
                <div className="h-7 bg-slate-200 rounded w-1/2 mb-2" />
                <div className="h-4 bg-slate-200 rounded w-1/3" />
              </div>
            </div>
            <div className="h-4 bg-slate-200 rounded w-full mb-2" />
            <div className="h-4 bg-slate-200 rounded w-3/4" />
          </div>
        </div>
        <div className="space-y-5">
          <div className="bg-white border border-slate-200 rounded-xl h-40" />
        </div>
      </div>
    </div>
  );
}

export default function AIToolDetailPage() {
  const { slug } = useParams();

  const [tool,     setTool]     = useState(null);
  const [related,  setRelated]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setTool(null);
    setRelated([]);

    fetchAIItem("tools", slug)
      .then((t) => {
        setTool(t);
        if (t?.category) {
          fetchAISection("tools", { page: 1, page_size: 8, category: t.category })
            .then((r) => {
              const items = Array.isArray(r) ? r : (r?.items ?? []);
              setRelated(items.filter((x) => (x.slug || x.id) !== slug).slice(0, 3));
            })
            .catch(() => {});
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <SkeletonDetail />;
  if (notFound || !tool) return <Navigate to="/ai-tools" replace />;

  const stars = Math.round((tool.score || 75) / 20);

  return (
    <>
      <SEO
        title={`${tool.name} — AI Tool Review & Trend Score · TrendMing`}
        description={`${tool.name} by ${tool.by}. ${tool.description} Pricing: ${tool.pricing}. Trend score: ${tool.score}/100.`}
        canonical={`https://trendming.com/ai-tools/${tool.slug}`}
        structuredData={[buildToolSchema(tool)]}
      />

      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-4">
          <nav aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-xs text-slate-400" itemScope itemType="https://schema.org/BreadcrumbList">
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <Link to="/" className="hover:text-slate-700 transition-colors focus:outline-none focus:text-blue-600" itemProp="item">
                  <span itemProp="name">Home</span>
                </Link>
                <meta itemProp="position" content="1" />
              </li>
              <span aria-hidden="true">/</span>
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <Link to="/ai-tools" className="hover:text-slate-700 transition-colors focus:outline-none focus:text-blue-600" itemProp="item">
                  <span itemProp="name">AI Tools</span>
                </Link>
                <meta itemProp="position" content="2" />
              </li>
              <span aria-hidden="true">/</span>
              <li className="text-slate-700 font-medium" aria-current="page" itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <span itemProp="name">{tool.name}</span>
                <meta itemProp="position" content="3" />
              </li>
            </ol>
          </nav>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          <main className="lg:col-span-2 space-y-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex items-center gap-4">
                  <span className="text-5xl" aria-hidden="true">{tool.emoji || "🤖"}</span>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h1 className="font-bold text-2xl md:text-3xl text-slate-900">{tool.name}</h1>
                      {tool.is_sponsored && (
                        <span className="text-[11px] font-bold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                          Sponsored
                        </span>
                      )}
                      {(tool.isNew || tool.is_new) && !tool.is_sponsored && (
                        <span className="text-[11px] font-bold uppercase tracking-wider text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">
                          New
                        </span>
                      )}
                    </div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mt-0.5">
                      {tool.category} · by {tool.by}
                    </p>
                  </div>
                </div>
                {tool.score != null && (
                  <span
                    className={`flex-shrink-0 text-xl font-bold px-3 py-1.5 border rounded-xl ${scoreColor(tool.score)}`}
                    aria-label={`Trend score: ${tool.score} out of 100`}
                  >
                    {tool.score}
                  </span>
                )}
              </div>

              <p className="text-slate-600 leading-relaxed mb-6">{tool.description}</p>

              <div className="flex flex-wrap gap-3">
                <Link
                  to={`/go/${tool.slug}`}
                  className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-5 py-2.5 text-sm rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-green-500/30"
                >
                  Try {tool.name} → <ArrowUpRight size={14} aria-hidden="true" />
                </Link>
                <Link
                  to="/ai-tools"
                  className="inline-flex items-center gap-2 border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold px-5 py-2.5 text-sm rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-slate-500/30"
                >
                  <ArrowLeft size={14} aria-hidden="true" /> Back to directory
                </Link>
              </div>
            </div>

            {(tool.highlights || []).length > 0 && (
              <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8">
                <h2 className="font-bold text-xl text-slate-900 mb-5">Key highlights</h2>
                <ul className="space-y-3">
                  {tool.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-3">
                      <CheckCircle size={16} className="text-blue-600 flex-shrink-0 mt-0.5" weight="fill" aria-hidden="true" />
                      <span className="text-sm text-slate-700">{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {tool.score != null && (
              <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-bold text-xl text-slate-900">Trend score</h2>
                  <div className="flex items-center gap-1">
                    <TrendUp size={14} className="text-blue-600" aria-hidden="true" />
                    <span className="text-sm font-bold text-blue-600">{tool.score} / 100</span>
                  </div>
                </div>
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden" role="progressbar" aria-valuenow={tool.score} aria-valuemin={0} aria-valuemax={100}>
                  <div className="h-full bg-blue-600 rounded-full transition-all" style={{ width: `${tool.score}%` }} />
                </div>
                <p className="mt-3 text-xs text-slate-400">
                  Based on news volume, social mentions, developer activity, and adoption signals.
                </p>
              </div>
            )}
          </main>

          <aside className="space-y-5">
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Tool info</h3>
              </div>
              <div className="divide-y divide-slate-100">
                {[
                  { label: "Category", value: tool.category },
                  { label: "Made by",  value: tool.by       },
                  { label: "Launched", value: tool.founded  },
                  { label: "Pricing",  value: tool.pricing  },
                ].filter((r) => r.value).map((row) => (
                  <div key={row.label} className="flex items-center justify-between px-5 py-3">
                    <span className="text-xs text-slate-400 font-medium">{row.label}</span>
                    <span className="text-xs font-semibold text-slate-700 text-right max-w-[60%]">{row.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {tool.score != null && (
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <div className="flex items-center gap-1 mb-2" aria-label={`Rating: ${(tool.score / 20).toFixed(1)} out of 5`}>
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} weight={i < stars ? "fill" : "regular"} className={i < stars ? "text-yellow-400" : "text-slate-200"} aria-hidden="true" />
                  ))}
                  <span className="text-xs font-bold text-slate-700 ml-1">{(tool.score / 20).toFixed(1)}</span>
                </div>
                <p className="text-xs text-slate-400">TrendMing trend-based score</p>
              </div>
            )}

            {related.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Related tools</h3>
                </div>
                <div className="divide-y divide-slate-100">
                  {related.map((t) => (
                    <Link
                      key={t.id || t.slug}
                      to={`/ai-tools/${t.slug || t.id}`}
                      className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50 transition-colors focus:outline-none focus:bg-blue-50"
                    >
                      <span className="text-lg" aria-hidden="true">{t.emoji || "🤖"}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">{t.name}</p>
                        <p className="text-[11px] text-slate-400 uppercase tracking-wider">{t.category}</p>
                      </div>
                      {t.score && <span className="text-xs font-bold text-slate-400">{t.score}</span>}
                    </Link>
                  ))}
                </div>
                <div className="px-5 py-3 border-t border-slate-100">
                  <Link
                    to={`/ai-tools?tab=${(tool.category || "").toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-xs font-semibold text-blue-600 hover:underline inline-flex items-center gap-1 focus:outline-none focus:underline"
                  >
                    All {tool.category} tools <ArrowRight size={12} aria-hidden="true" />
                  </Link>
                </div>
              </div>
            )}
          </aside>
        </div>

        {related.length > 0 && (
          <section className="mt-10 pt-8 border-t border-slate-200">
            <h2 className="font-bold text-2xl text-slate-900 mb-6">Recommended for you</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {related.slice(0, 3).map((t) => (
                <RecommendedToolCard key={t.slug || t.id} tool={t} />
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}
